import {AnswerStatus} from '@domain/pom'
import {Comments, CommonComment} from '@hconnect/common/comments'
import CloseIcon from '@mui/icons-material/Close'
import {Typography, Box, Theme, Stack, IconButton, Link} from '@mui/material'
import {toLower} from 'lodash'
import moment from 'moment-timezone'
import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'
import {
  useAddComment,
  useAddQuestionAttachments,
  useComments,
  useDeleteComment,
  useDeleteQuestionAttachment,
  useEditComment,
  usePlantUpmId,
  useTimeZone
} from '../../hooks'
import {PomComment, QuestionItem} from '../../types'
import {ANSWER_STATES, colorByAnswer} from '../../utils'
import {Attachments} from '../common'

import {Actions} from './Actions'
import {AnswerButton} from './AnswerButton'
import {AnswerIcon} from './AnswerIcon'
import {QuestionsText} from './QuestionsText'

interface QuestionDetailProps {
  selectedQuestion: QuestionItem
  onClose: () => void
  onSelected: (id: string, answer: AnswerStatus) => void
}

export const mapComments = (comments?: PomComment[]): CommonComment[] | undefined =>
  comments?.map((comment) => ({
    text: comment.value,
    id: comment.id,
    createdBy: comment.createdBy,
    updatedBy: comment.updatedBy,
    createdOn: moment(comment.createdOn),
    updatedOn: comment.updatedOn ? moment(comment.updatedOn) : undefined
  }))

export const QuestionDetail: React.FC<QuestionDetailProps> = (props) => {
  const {selectedQuestion, onClose, onSelected} = props
  const {answer: selectedAnswer, id, questions, guidanceLink} = selectedQuestion
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const timeZone = useTimeZone()
  const plantId = usePlantUpmId()

  const {data: comments} = useComments(id)
  const {mutate, isPending} = useAddComment()

  const data: CommonComment[] | undefined = useMemo(() => mapComments(comments), [comments])

  const {mutate: deleteComment, isPending: deleting} = useDeleteComment()
  const {mutate: saveEditedComment, isPending: editSaving} = useEditComment()
  const {mutate: addAttachments, isPending: uploading} = useAddQuestionAttachments()
  const {mutate: deleteAttachment} = useDeleteQuestionAttachment()

  const onCommentDelete = useCallback(
    (id: string) => {
      const commentToDelete = comments?.find((comment) => comment.id === id)
      if (commentToDelete) {
        deleteComment(commentToDelete)
      }
    },
    [comments, deleteComment]
  )

  const onEditCommentSave = useCallback(
    (id: string, value: string) => {
      const commentToEdit = comments?.find((comment) => comment.id === id)
      if (commentToEdit) {
        saveEditedComment({...commentToEdit, value})
      }
    },
    [comments, saveEditedComment]
  )

  return (
    <Box mx={1.5} data-test-id="question-detail">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography
          variant="caption"
          sx={(theme: Theme) => ({
            color: theme.palette.grey[500]
          })}
        >
          {id}
        </Typography>
        <IconButton onClick={onClose} data-test-id="question-detail-close">
          <CloseIcon />
        </IconButton>
      </Box>
      <Typography variant="h3">
        <QuestionsText questions={questions} />
      </Typography>
      {guidanceLink && (
        <Link
          href={guidanceLink}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          data-test-id="question-detail-link"
        >
          {t('questionnaire.referenceLink')}
        </Link>
      )}
      <Stack mt={3} spacing={1.5} direction="row">
        {ANSWER_STATES.map((answer: AnswerStatus) => (
          <AnswerButton
            key={answer}
            color={colorByAnswer(answer)}
            startIcon={<AnswerIcon answer={answer} />}
            text={t(`answers.${answer}`)}
            isSelected={answer === selectedAnswer}
            onClick={() => onSelected(id, answer)}
            data-test-id={`question-detail-btn-${toLower(answer)}`}
          />
        ))}
      </Stack>
      <Box>
        <Actions question={selectedQuestion} />
        <Comments
          translate={t}
          language={i18n.language}
          data={data}
          isLoading={isPending}
          deleteCommentProps={{
            onDelete: onCommentDelete,
            deletingInProgress: deleting
          }}
          onAddComment={(value) => mutate({questionId: id, comment: value})}
          editCommentProps={{
            savingChangesInProgress: editSaving,
            onEdit: onEditCommentSave
          }}
          timeZone={timeZone}
        />
        <Attachments
          attachedFiles={selectedQuestion.attachments}
          onUpload={(files) => addAttachments({plantId, questionId: id, files})}
          onDelete={(attachment) => deleteAttachment({plantId, questionId: id, attachment})}
          isUploading={uploading}
          accept="image/*, application/pdf, application/msword, application/vnd.ms-excel"
        />
      </Box>
    </Box>
  )
}
